import { Tag } from "antd";
import Broker from "components/Message/Broker";
import IntlMessages from "components/Message/IntlMessages";
import ReceiveMethodMessage from "components/Message/ReceiveMethodMessage";
import { ReceiveMethod, SecurityType, OfferType, UnitDisplay, ErrorType, PaymentMethod, CouponType } from "const";
import { clazz, isEmpty } from "./common-util";
import { calculate, formatNumber, workAroundNumber } from "./math";
import indexOf from "lodash/indexOf";
import find from "lodash/find";
import compact from "lodash/compact";
import ErrorMessageConst from "components/Message/ErrorMessageConst";
import { Fragment } from "react";
import Income from "components/Message/Income";
import { isMainTitleAndGenderMatched, isTitleOther } from "./customer-util";
import { USER_INPUT_EMAIL_REGEX } from "./form";
import moment from "moment-timezone";

export const isUnitDisplay = (unitDisplay) => unitDisplay === UnitDisplay.UNIT;
export const isBahtDisplay = (unitDisplay) => unitDisplay === UnitDisplay.BAHT;
export const checkSecurityType = (productOrSecurityType, comparedType) => {
  return typeof productOrSecurityType === "object"
    ? productOrSecurityType.securityType === comparedType
    : productOrSecurityType === comparedType;
};
export const isSecurityTypeUNT = (productOrSecurityType) => checkSecurityType(productOrSecurityType, SecurityType.UNT);
export const isSecurityTypeDBT = (productOrSecurityType) => checkSecurityType(productOrSecurityType, SecurityType.DBT);
export const isSecurityTypeSTO = (productOrSecurityType) => checkSecurityType(productOrSecurityType, SecurityType.STO);
export const isSecurityTypeCOM = (productOrSecurityType) => checkSecurityType(productOrSecurityType, SecurityType.COM);

export const checkOfferType = (productOrOfferType, comparedType) => {
  return typeof productOrOfferType === "object"
    ? productOrOfferType.offerType === comparedType
    : productOrOfferType === comparedType;
};
export const isPO = (productOrOfferType) => checkOfferType(productOrOfferType, OfferType.PO);
export const isRO = (productOrOfferType) => checkOfferType(productOrOfferType, OfferType.RO);

export const checkCouponType = (productOrSecurityType, couponType) => {
  return typeof productOrSecurityType === "object"
    ? productOrSecurityType.couponType === couponType
    : productOrSecurityType === couponType;
};

export const isCouponTypeZERO = (productOrSecurityType) => checkCouponType(productOrSecurityType, CouponType.ZERO);

export const getUnitType = (productOrSecurityType) =>
  isSecurityTypeSTO(productOrSecurityType) ? (
    <IntlMessages id="global.unit.common-stock" />
  ) : (
    <IntlMessages id="global.unit.default" />
  );

export const unitToBaht = (unit, sellPriceUnit) => {
  unit = workAroundNumber(unit);
  sellPriceUnit = workAroundNumber(sellPriceUnit);
  return calculate(`${unit} * ${sellPriceUnit}`);
};

export const bahtToUnit = (baht, sellPriceUnit) => {
  baht = workAroundNumber(baht);
  sellPriceUnit = workAroundNumber(sellPriceUnit);
  return calculate(`${baht} / ${sellPriceUnit}`, 8);
};

export const UnitValidator = {
  validateMinAndMultiple: (unit, productDetail) => {
    const vMultiple = UnitValidator.validMultiple(unit, productDetail);
    const vMinimum = UnitValidator.validMinimum(unit, productDetail);
    return vMultiple && vMinimum;
  },
  validMultiple: (unit, productDetail) => {
    unit = workAroundNumber(unit);
    const multiple = workAroundNumber(productDetail.incrementUnit);
    return multiple === 0 || (unit > 0 && unit % multiple === 0);
  },
  validMinimum: (unit, productDetail) => {
    unit = workAroundNumber(unit);
    const min = workAroundNumber(productDetail.minimumUnit);
    const multiple = workAroundNumber(productDetail.incrementUnit);
    return multiple === 0 || (unit > 0 && unit >= min);
  },
  validMaxUnit: (unit, productDetail) => {
    unit = workAroundNumber(unit);
    const maxUnit = productDetail.remainingUnit ? workAroundNumber(productDetail.remainingUnit) : Infinity;
    return unit <= maxUnit;
  },
  validMaxPerTxn: (unit, productDetail) => {
    unit = workAroundNumber(unit);
    const maxUnit = workAroundNumber(productDetail.maxUnitPerTxn) || Infinity;
    return unit <= maxUnit;
  },
  validOneShotZeroIncrement: (unit, productDetail) => {
    unit = workAroundNumber(unit);
    const multiple = workAroundNumber(productDetail.incrementUnit);
    return multiple > 0 || (multiple === 0 && unit === productDetail.minimumUnit);
  },
  validRemainUnit: (unit, _, unitHolder) => {
    if (!unitHolder) return false;
    unit = workAroundNumber(unit);
    const remainUnit = workAroundNumber(unitHolder.remainUnit);
    return unit <= remainUnit;
  },
};

export const BahtParValidator = {
  validateMinAndMultiple: (baht, productDetail) => {
    const vMultiple = BahtParValidator.validMultiple(baht, productDetail);
    const vMinimum = BahtParValidator.validMinimum(baht, productDetail);
    return vMultiple && vMinimum;
  },
  validMultiple: (baht, productDetail) => {
    baht = workAroundNumber(baht);
    const unit = bahtToUnit(baht, productDetail.initPar);
    return UnitValidator.validMultiple(unit, productDetail);
  },
  validMinimum: (baht, productDetail) => {
    baht = workAroundNumber(baht);
    const unit = bahtToUnit(baht, productDetail.initPar);
    return UnitValidator.validMinimum(unit, productDetail);
  },
  validMaxUnit: (baht, productDetail) => {
    baht = workAroundNumber(baht);
    const unit = bahtToUnit(baht, productDetail.initPar);
    return UnitValidator.validMaxUnit(unit, productDetail);
  },
  validMaxPerTxn: (baht, productDetail) => {
    baht = workAroundNumber(baht);
    const unit = bahtToUnit(baht, productDetail.initPar);
    return UnitValidator.validMaxPerTxn(unit, productDetail);
  },
  validOneShotZeroIncrement: (baht, productDetail) => {
    baht = workAroundNumber(baht);
    const unit = bahtToUnit(baht, productDetail.initPar);
    return UnitValidator.validOneShotZeroIncrement(unit, productDetail);
  },
};

export const validateBalancePaymentAccount = (totalPayment, account, paymentMethod = "payNow") => {
  if (paymentMethod === "payNow") {
    const valid = calculate(`${account.availBalAmt} - ${totalPayment}`) >= 0;
    return valid;
  }
  return true;
};

export const validateAddress = (data) => {
  return (
    data.mailingAddress &&
    data.mailingProvince &&
    data.mailingDistrict &&
    data.mailingSubDistrict &&
    data.mailingZipCode &&
    data.mailingZipCode.length === 5
  );
};
export const validateIncomeSource = (data) => {
  return !!data.incomeSource;
};
export const validateEmail = (data) => !!(data.email && USER_INPUT_EMAIL_REGEX.test(data.email));
export const validateBroker = (data) => {
  return !!data.receiveSecurityCompanyCode && !!data.receiveAccountNumber;
};
export const validateReceiveMethod = (data) => {
  let receiveMethodValid;
  switch (data.receiveMethod) {
    case ReceiveMethod.BROKER:
      receiveMethodValid = validateBroker(data);
      break;
    case ReceiveMethod.KTB:
      receiveMethodValid = true;
      break;
    default:
      receiveMethodValid = false;
  }

  return receiveMethodValid;
};

export function renderReceiveMethodLabel(data, brokerCode, options = {}) {
  const { brokerClass, KtbClass } = options;
  const receiveMethod = data.receiveMethod;
  return (
    <>
      {ReceiveMethod.BROKER === receiveMethod && (
        <>
          <div className="mb-1">
            <Tag className="tag-main me-0">
              <IntlMessages id="receive.methods.securities-company" />
            </Tag>
          </div>
          <div className={clazz("color-label", brokerClass)}>
            <Broker code={brokerCode ?? data.receiveSecurityCompanyCode} />
          </div>
        </>
      )}
      <span className={clazz(KtbClass)}>
        {ReceiveMethod.KTB === receiveMethod && <ReceiveMethodMessage code={ReceiveMethod.KTB} />}
      </span>
    </>
  );
}

export function getSettlementDateByDate(date, settlementList) {
  if (!date) return;
  const currentDate = +moment(date).startOf("day");
  return settlementList?.find(({ settlementDate }) => currentDate === +moment(settlementDate).startOf("day"));
}

export function getAccountByAccountNo(accNo, accounts) {
  if (!accNo) return;
  return accounts?.find((a) => a.accountId === accNo);
}

export function getUnitHoldersByAcId(acId, unitHolders) {
  if (!acId) return;
  return unitHolders?.find((u) => u.acId === acId);
}

export function displayIncomeSource(incomeSource, otherDesc) {
  // prepare
  incomeSource = (Array.isArray(incomeSource) ? incomeSource : incomeSource?.trim().split("|")) ?? [];
  // move other to last
  const othIndex = indexOf(incomeSource, "OTH");
  const hasOtherChoice = othIndex !== -1;
  if (hasOtherChoice) {
    incomeSource.splice(othIndex, 1);
    incomeSource.push("OTH");
  }
  // render
  return (
    <>
      {incomeSource
        .filter((v) => v)
        .map((source, i, arr) => (
          <Fragment key={source}>
            <Income code={source} />
            {i !== arr.length - 1 && <span>, </span>}
          </Fragment>
        ))}
      {hasOtherChoice && otherDesc && ` | ${otherDesc}`}
    </>
  );
}

export const ruleUnits = (unitDisplay, productDetail, isCustomerBuy) => {
  const validOneShotZeroIncrement = (Validator) => ({
    validator: (_, amount) =>
      Validator.validOneShotZeroIncrement(amount, productDetail) ? Promise.resolve() : Promise.reject(),
    message: <IntlMessages id="second.market.validate.zero-inc" values={{ unit: productDetail.minimumUnit }} />,
  });

  const validateMinAndMultiple = (Validator) => ({
    validator: (_, amount) =>
      Validator.validateMinAndMultiple(amount, productDetail) ? Promise.resolve() : Promise.reject(),
    message: isPO(productDetail) ? (
      <ErrorMessageConst code={ErrorType.INVALID_UNIT_PO} />
    ) : (
      <ErrorMessageConst code={ErrorType.INVALID_UNIT_RO} />
    ),
  });

  const validateMaxUnit = (Validator) =>
    productDetail.remainingUnit && {
      validator: (_, amount) => (Validator.validMaxUnit(amount, productDetail) ? Promise.resolve() : Promise.reject()),
      message: (
        <>
          <IntlMessages
            id={isCustomerBuy ? "validate.remainging.bank-offer" : "validate.remainging.bank-bid"}
            values={{
              amount: formatNumber(
                Validator === UnitValidator ? productDetail.remainingUnit : productDetail.remainingUnitAmt
              ),
              unitDisplay:
                Validator === UnitValidator ? getUnitType(productDetail) : <IntlMessages id="global.unit.baht" />,
            }}
          />
        </>
      ),
    };

  const validMaxUnitPerTxn = (Validator) => {
    if (!productDetail.maxUnitPerTxn) {
      return null;
    }

    const validator = (_, amount) =>
      Validator.validMaxPerTxn(amount, productDetail) ? Promise.resolve() : Promise.reject();

    if (Validator === UnitValidator) {
      return {
        validator,
        message: (
          <IntlMessages
            id="second.market.validate.max-unit-per-txn"
            values={{ unit: formatNumber(productDetail.maxUnitPerTxn) }}
          />
        ),
      };
    }

    return {
      validator,
      message: (
        <IntlMessages
          id="second.market.validate.max-amt-per-txn"
          values={{ baht: formatNumber(calculate(`${productDetail.maxUnitPerTxn} * ${productDetail.initPar}`, 0)) }}
        />
      ),
    };
  };

  const Validator = isBahtDisplay(unitDisplay) ? BahtParValidator : UnitValidator;
  return compact([
    validOneShotZeroIncrement(Validator),
    validateMinAndMultiple(Validator),
    validMaxUnitPerTxn(Validator),
    validateMaxUnit(Validator),
  ]);
};

export const isTitleAndGenderValid = (subscriptionDetail, titleOthers) => {
  let isValid = true;

  if (subscriptionDetail.isGenderAndTitleInvalid) {
    if (isTitleOther(subscriptionDetail.title)) {
      const titleOtherOption = find(titleOthers, { titleDesc: subscriptionDetail.titleOther });
      isValid =
        titleOtherOption &&
        (isEmpty(titleOtherOption.sexCode) || titleOtherOption.sexCode === subscriptionDetail.gender);
    } else {
      isValid = isMainTitleAndGenderMatched(subscriptionDetail.gender, subscriptionDetail.title);
    }
  }

  return isValid;
};

export const SubscriptionValidator = {
  isSelectAccountUnitPageValid: (subscriptionDetail, accounts) => {
    const { accountNo, baht } = subscriptionDetail;
    const paymentAccountValid = !!accountNo;
    const paymentAccountBalanceValid =
      accountNo &&
      accounts &&
      accounts.length > 0 &&
      (() => {
        if (subscriptionDetail.useLombard) return true;

        const paymentAccount = getAccountByAccountNo(accountNo, accounts);
        return validateBalancePaymentAccount(baht, paymentAccount, PaymentMethod.payNow);
      })();

    const receiveMethodValid = subscriptionDetail.useLombard || validateReceiveMethod(subscriptionDetail);

    return paymentAccountValid && paymentAccountBalanceValid && receiveMethodValid;
  },
  isConfirmationPageValid: (subscriptionDetail, titleOthers) => {
    const emailValid = validateEmail(subscriptionDetail);
    const titleAndGenderValid = isTitleAndGenderValid(subscriptionDetail, titleOthers);
    return emailValid && titleAndGenderValid;
  },
};

export const calculateTotalWithTax = (unit, dirtyPriceValue, tax, costValue) => {
  unit = workAroundNumber(unit);
  costValue = workAroundNumber(costValue);
  dirtyPriceValue = workAroundNumber(dirtyPriceValue);
  tax = workAroundNumber(tax);

  const costAmount = calculate(`${unit} * ${costValue}`, 2);
  const dirtyAmount = calculate(`${unit} * ${dirtyPriceValue}`, 2);
  const profit = calculate(`${dirtyAmount} - ${costAmount}`, 2);
  const totalTax = profit > 0 ? calculate(`${profit} * ${tax} / 100`, 2) : 0;
  const totalReceive = calculate(`${unit} * ${dirtyPriceValue}`, 2);
  const net = calculate(`${totalReceive} - ${totalTax}`, 2);

  return {
    unit,
    totalTax,
    totalReceive,
    net,
  };
};

export const calculateWht = (unit, dirtyPriceValue, withholdingTax, initPar, parValue) => {
  unit = workAroundNumber(unit);
  dirtyPriceValue = workAroundNumber(dirtyPriceValue);
  withholdingTax = workAroundNumber(withholdingTax);
  parValue = workAroundNumber(parValue);
  initPar = workAroundNumber(initPar);

  const dirtyAmount = calculate(`${unit} * ${dirtyPriceValue}`, 2);
  const gainLoss = calculate(`(${unit} * ${parValue}) - ${dirtyAmount}`, 2);
  const wht = gainLoss > 0 ? Math.max(calculate(`(${gainLoss} * ${withholdingTax}) / 100`, 2), 0) : 0;
  const net = calculate(`${dirtyAmount} + ${wht}`, 2);

  const unitBaht = calculate(`${unit} * ${initPar}`, 2);

  return {
    dirtyAmount,
    gainLoss,
    wht,
    baht: net,
    unitBaht,
  };
};

export const getBrokerageAccountDataForBuy = (brokerageAcc) => ({
  receiveMethod: brokerageAcc?.receiveMethod,
  receiveAccountNumber: brokerageAcc?.brokerageAccNo,
  brokerageAccNo: brokerageAcc?.brokerageAccNo,
  receiveSecurityCompanyCode: brokerageAcc?.securitiesCompCode,
});

export const getBrokerageAccountDataForSell = (brokerageAcc) => ({
  receiveMethod: brokerageAcc?.receiveMethod,
  receiveAccountNumber: brokerageAcc?.brokerageAccNo,
  brokerageAccNo: brokerageAcc?.brokerageAccNo,
  receiveSecurityCompanyCode: brokerageAcc?.securitiesCompCode,
  custAmt: brokerageAcc?.custAmt,
  custUnit: brokerageAcc?.custUnit,
  freezeUnit: brokerageAcc?.freezeUnit,
  pledgeUnit: brokerageAcc?.pledgeUnit,
  custParValue: brokerageAcc?.parValue,
  custAvailUnit: brokerageAcc?.availUnit,
});
